import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { TILLS, MODES, TILL_LABEL, TERMINAL, ZLINE, CARDLINE } from "@/lib/constants";
import { Sunrise, Moon, RefreshCw, Calculator, ChevronRight, History, ChevronLeft, UserCog, Clock, Camera } from "lucide-react";
import { clearSession, getRole, getName, listCloses } from "@/lib/api";
import { formatDate, money } from "@/lib/format";

const ICONS = { sunrise: Sunrise, moon: Moon, refresh: RefreshCw, calculator: Calculator };

export default function Start() {
  const nav = useNavigate();
  const [till, setTill] = useState(null);
  const [latest, setLatest] = useState(null);
  const role = getRole();

  useEffect(() => {
    if (role !== "owner") return;
    listCloses({ limit: 1 }).then((rows) => setLatest(rows[0] || null)).catch(() => {});
  }, [role]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
      <Header onBack={till ? () => setTill(null) : undefined} />
      <main className="max-w-md mx-auto px-5 pb-14 pt-6 space-y-5">
        {!till ? (
          <>
            <div>
              <div className="text-[10px] tracking-[0.22em] text-[#5C7566] uppercase">Step 1 of 2</div>
              <h2 className="text-3xl text-[#0D311E] mt-1" style={{ fontFamily: "'Fraunces', serif" }}>
                Which till are you closing?
              </h2>
              <p className="text-[#2B533E] mt-2">Pick one. I'll set it up for that till.</p>
            </div>

            {role === "owner" && latest && (
              <button
                data-testid="open-latest"
                onClick={() => nav(`/close-detail/${latest.id}`)}
                className="w-full bg-white rounded-2xl border border-[#E8E4DA] p-4 text-left hover:border-[#1A5D3A] active:scale-[0.99] transition-all flex items-center gap-3"
              >
                <div className="h-10 w-10 rounded-xl bg-[#F5F2EA] flex items-center justify-center shrink-0">
                  <Clock size={16} className="text-[#1A5D3A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-[0.22em] text-[#5C7566] uppercase">Latest close</div>
                  <div className="text-[#0D311E] font-medium truncate">
                    {TILL_LABEL[latest.till] || latest.till} · {String(latest.mode).replace(/_/g, " ")}
                  </div>
                  <div className="text-xs text-[#5C7566] truncate">{formatDate(latest.created_at)}</div>
                </div>
                <LatestDiffBadge diff={Number(latest.difference || 0)} />
                <ChevronRight size={16} className="text-[#5C7566] shrink-0" />
              </button>
            )}

            {TILLS.map((t) => (
              <button
                key={t}
                data-testid={`till-select-${t.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setTill(t)}
                className="w-full bg-white rounded-2xl border border-[#E8E4DA] p-5 text-left hover:border-[#1A5D3A] active:scale-[0.99] transition-all shadow-sm group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-2xl text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>
                      {TILL_LABEL[t]}
                    </div>
                    <div className="text-sm text-[#5C7566] mt-1">
                      Drawer line <span className="text-[#0D311E] font-medium">{ZLINE[t]}</span> · Card line {" "}
                      <span className="text-[#0D311E] font-medium">{CARDLINE[t]}</span>
                    </div>
                    <div className="text-xs text-[#5C7566] mt-1">Terminal {TERMINAL[t]}</div>
                  </div>
                  <ChevronRight size={20} className="text-[#5C7566] group-hover:text-[#1A5D3A] mt-1" />
                </div>
              </button>
            ))}

            <button
              data-testid="attach-photos-home"
              onClick={() => nav("/attach-photos")}
              className="w-full bg-[#F5F2EA] rounded-2xl border border-[#E8E4DA] p-4 flex items-center justify-between hover:bg-[#EBE7DF] active:scale-[0.99]"
            >
              <div className="flex items-center gap-3">
                <Camera size={18} className="text-[#2B533E]" />
                <div className="text-left">
                  <div className="text-[#0D311E] font-medium">Add receipt photos</div>
                  <div className="text-xs text-[#5C7566]">Forgot the pics? Attach them to a finished close.</div>
                </div>
              </div>
              <ChevronRight size={18} className="text-[#5C7566] shrink-0" />
            </button>

            <button
              data-testid="open-dashboard"
              onClick={() => nav("/dashboard")}
              className="w-full bg-[#F5F2EA] rounded-2xl border border-[#E8E4DA] p-4 flex items-center justify-between hover:bg-[#EBE7DF] active:scale-[0.99]"
            >
              <div className="flex items-center gap-3">
                <History size={18} className="text-[#2B533E]" />
                <span className="text-[#0D311E] font-medium">Past closes & export</span>
              </div>
              <ChevronRight size={18} className="text-[#5C7566]" />
            </button>
            {role !== "owner" && (
              <p className="text-xs text-[#5C7566] text-center">Owner PIN required to view the full list.</p>
            )}

            <button
              data-testid="switch-user"
              onClick={() => { clearSession(); nav("/login", { replace: true }); }}
              className="w-full mt-2 rounded-2xl border border-dashed border-[#E8E4DA] p-4 flex items-center justify-center gap-2 text-[#2B533E] hover:bg-[#F5F2EA] active:scale-[0.99]"
            >
              <UserCog size={16} />
              <span className="text-sm">
                Switch user — currently <strong>{role === "owner" ? "Owner" : (getName() || "Staff")}</strong>
              </span>
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm text-[#2B533E]">
              <button data-testid="mode-back" onClick={() => setTill(null)} className="flex items-center gap-1 hover:underline">
                <ChevronLeft size={14} /> {TILL_LABEL[till]}
              </button>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.22em] text-[#5C7566] uppercase">Step 2 of 2</div>
              <h2 className="text-3xl text-[#0D311E] mt-1" style={{ fontFamily: "'Fraunces', serif" }}>
                What are you doing?
              </h2>
            </div>

            {MODES.map((m) => {
              const Icon = ICONS[m.icon];
              return (
                <button
                  key={m.id}
                  data-testid={`mode-${m.id}`}
                  onClick={() => nav(`/close/${encodeURIComponent(till)}/${m.id}`)}
                  className="w-full bg-white rounded-2xl border border-[#E8E4DA] p-4 text-left hover:border-[#1A5D3A] active:scale-[0.99] transition-all shadow-sm flex items-start gap-4"
                >
                  <div className="h-11 w-11 rounded-xl bg-[#F5F2EA] flex items-center justify-center text-[#1A5D3A] shrink-0">
                    <Icon size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-lg text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>
                      {m.label}
                    </div>
                    <div className="text-sm text-[#5C7566] mt-0.5">{m.blurb}</div>
                  </div>
                  <ChevronRight size={18} className="text-[#5C7566] mt-3" />
                </button>
              );
            })}
          </>
        )}
      </main>
    </div>
  );
}

function LatestDiffBadge({ diff }) {
  const balanced = Math.abs(diff) < 0.005;
  const big = Math.abs(diff) > 10;
  const tone = balanced ? "bg-[#1B7B4B]/10 text-[#155F39]" : big ? "bg-[#B91C1C]/10 text-[#7F1D1D]" : "bg-[#D97706]/15 text-[#92520A]";
  return (
    <span className={`px-2 py-1 rounded-full text-[10px] tabular-nums shrink-0 ${tone}`}>
      {balanced ? "Balanced" : `${diff > 0 ? "Over" : "Short"} ${money(Math.abs(diff))}`}
    </span>
  );
}
